import { useState, useEffect } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmtK = (v) => {
  if (!v && v !== 0) return '-';
  const n = typeof v === 'number' ? v : parseFloat(v);
  return `₹${(n / 1000).toFixed(2)}`;
};

const fmtAbs = (v) => {
  if (!v && v !== 0) return '-';
  return formatCurrency(v);
};

const LineItem = ({ label, current, previous, indent = 0, bold = false, negative = false }) => (
  <tr className={`${bold ? 'font-semibold bg-[#F8FAFC]' : 'hover:bg-[#F8FAFC]'} border-b border-[#E2E8F0]`}>
    <td className="py-2.5 px-4 text-sm" style={{ paddingLeft: `${16 + indent * 16}px` }}>
      {label}
    </td>
    <td className={`py-2.5 px-4 text-sm text-right font-mono ${negative && current < 0 ? 'text-[#DC2626]' : ''}`}>
      {current !== undefined && current !== null ? fmtK(current) : '-'}
    </td>
    <td className={`py-2.5 px-4 text-sm text-right font-mono text-[#64748B] ${negative && previous < 0 ? 'text-[#DC2626]' : ''}`}>
      {previous !== undefined && previous !== null ? fmtK(previous) : '-'}
    </td>
  </tr>
);

export default function FinancialStatementsPage() {
  const [statements, setStatements] = useState([]);
  const [currentMetrics, setCurrentMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('2025-26');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [stmtRes, metricsRes] = await Promise.all([
        api.get('/reports/financial-statements'),
        api.get('/reports/current-year-metrics'),
      ]);
      setStatements(stmtRes.data.statements || []);
      setCurrentMetrics(metricsRes.data);
    } catch (error) {
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const stmt = statements.find(s => s.fiscal_year === selectedYear);
  const prevStmt = statements.find(s => s.fiscal_year === '2023-24');
  const pl = stmt?.profit_loss || {};
  const prevPl = prevStmt?.profit_loss || {};
  const bs = stmt?.balance_sheet || {};
  const prevBs = prevStmt?.balance_sheet || {};
  const ratios = stmt?.ratios || {};
  const prevRatios = prevStmt?.ratios || {};

  if (loading) {
    return (
      <div data-testid="financial-statements-page">
        <PageHeader title="Financial Statements" subtitle="Loading..." />
        <div className="page-content"><div className="skeleton h-96 rounded-lg" /></div>
      </div>
    );
  }

  return (
    <div data-testid="financial-statements-page">
      <PageHeader
        title="Financial Statements"
        subtitle="GANG OH INNOVATIONS PRIVATE LIMITED"
        actions={
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-32" data-testid="fy-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statements.map(s => (
                <SelectItem key={s.fiscal_year} value={s.fiscal_year}>FY {s.fiscal_year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <div className="page-content">
        {/* Current Year Metrics Banner */}
        {currentMetrics && (
          <div className="bg-[#0F4C81] text-white rounded-lg p-5 mb-6" data-testid="current-year-banner">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold font-['Plus_Jakarta_Sans']">Current FY {currentMetrics.fiscal_year} (Live from Zoho)</h3>
              <span className="text-xs opacity-70">{currentMetrics.period?.start} to {currentMetrics.period?.end}</span>
            </div>
            <div className="grid grid-cols-5 gap-4">
              <div>
                <p className="text-xs opacity-70">Revenue</p>
                <p className="text-lg font-bold font-['Plus_Jakarta_Sans']">{fmtAbs(currentMetrics.revenue_from_operations)}</p>
              </div>
              <div>
                <p className="text-xs opacity-70">GST Collected</p>
                <p className="text-lg font-bold font-['Plus_Jakarta_Sans']">{fmtAbs(currentMetrics.gst_collected)}</p>
              </div>
              <div>
                <p className="text-xs opacity-70">Collections</p>
                <p className="text-lg font-bold font-['Plus_Jakarta_Sans']">{fmtAbs(currentMetrics.collections)}</p>
              </div>
              <div>
                <p className="text-xs opacity-70">Outstanding</p>
                <p className="text-lg font-bold font-['Plus_Jakarta_Sans'] text-orange-300">{fmtAbs(currentMetrics.outstanding)}</p>
              </div>
              <div>
                <p className="text-xs opacity-70">Invoices</p>
                <p className="text-lg font-bold font-['Plus_Jakarta_Sans']">{currentMetrics.invoice_count?.toLocaleString()}</p>
              </div>
            </div>
            {currentMetrics.source_breakdown?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-white/20">
                <p className="text-xs opacity-70 mb-2">Revenue by Source</p>
                <div className="flex gap-4 flex-wrap">
                  {currentMetrics.source_breakdown.map((s, i) => (
                    <div key={i} className="bg-white/10 rounded px-3 py-1.5">
                      <span className="text-xs opacity-80">{s.source}: </span>
                      <span className="text-sm font-medium">{fmtAbs(s.revenue)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!stmt ? (
          <div className="text-center py-16 text-[#64748B]">
            <p>No financial statement available for FY {selectedYear}</p>
          </div>
        ) : (
          <>
            {stmt.is_live && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 text-sm text-amber-800" data-testid="live-data-banner">
                <span className="font-medium">Live Data</span> — {stmt.data_source || 'Generated from Zoho invoices in real-time.'}
              </div>
            )}
            {stmt.vendor_expenses?.length > 0 && (
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 mb-4" data-testid="vendor-expense-summary">
                <h4 className="text-sm font-semibold text-[#0F172A] mb-3">Vendor Expenses (from AI-parsed invoices)</h4>
                <div className="grid grid-cols-3 gap-4">
                  {stmt.vendor_expenses.map((ve, i) => (
                    <div key={i} className="bg-[#F8FAFC] rounded-lg p-3">
                      <p className="text-xs text-[#64748B]">{ve.vendor}</p>
                      <p className="text-lg font-bold text-[#0F172A]">{fmtAbs(ve.subtotal)}</p>
                      <p className="text-xs text-[#64748B]">GST: {fmtAbs(ve.gst)} | Total: {fmtAbs(ve.total)}</p>
                    </div>
                  ))}
                  <div className="bg-[#0F4C81] text-white rounded-lg p-3">
                    <p className="text-xs opacity-70">Total Vendor Expenses</p>
                    <p className="text-lg font-bold">{fmtAbs(stmt.vendor_expenses.reduce((s, v) => s + (v.subtotal || 0), 0))}</p>
                    <p className="text-xs opacity-70">excl. GST Input Credit</p>
                  </div>
                </div>
              </div>
            )}
            <Tabs defaultValue="pnl">
            <TabsList className="mb-4">
              <TabsTrigger value="pnl" data-testid="tab-pnl">Profit & Loss</TabsTrigger>
              <TabsTrigger value="balance-sheet" data-testid="tab-bs">Balance Sheet</TabsTrigger>
              <TabsTrigger value="ratios" data-testid="tab-ratios">Key Ratios</TabsTrigger>
            </TabsList>

            <TabsContent value="pnl">
              <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="pnl-statement">
                <div className="bg-[#0F4C81] text-white px-4 py-3">
                  <h3 className="font-semibold text-sm">Statement of Profit and Loss</h3>
                  <p className="text-xs opacity-70">All figures in ₹ Thousands</p>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#E2E8F0]">
                      <th className="text-left px-4 py-2 text-xs font-medium text-[#64748B]">Particulars</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-[#64748B]">FY {selectedYear}</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-[#64748B]">FY 2023-24</th>
                    </tr>
                  </thead>
                  <tbody>
                    <LineItem label="I. Revenue from Operations" current={pl.revenue_from_operations} previous={prevPl.revenue_from_operations} bold />
                    <LineItem label="Sale of Goods" current={pl.sales_of_goods} previous={prevPl.sales_of_goods} indent={1} />
                    <LineItem label="Sale of Services" current={pl.sales_of_services} previous={prevPl.sales_of_services} indent={1} />
                    <LineItem label="II. Other Income" current={pl.other_income} previous={prevPl.other_income} />
                    <LineItem label="III. Total Revenue (I+II)" current={pl.total_revenue} previous={prevPl.total_revenue} bold />
                    <LineItem label="IV. EXPENSES" current={null} previous={null} bold />
                    <LineItem label="Cost of Material Consumed" current={pl.cost_of_material_consumed} previous={prevPl.cost_of_material_consumed} indent={1} />
                    <LineItem label="Purchases of Stock-in-Trade" current={pl.purchase_of_trade_goods} previous={prevPl.purchase_of_trade_goods} indent={2} />
                    <LineItem label="Changes in Inventories" current={pl.change_in_inventories} previous={prevPl.change_in_inventories} indent={2} negative />
                    <LineItem label="Employee Benefit Expenses" current={pl.employee_benefit_expenses} previous={prevPl.employee_benefit_expenses} indent={1} />
                    <LineItem label="Salaries, Wages & Bonus" current={pl.salaries_wages_bonus} previous={prevPl.salaries_wages_bonus} indent={2} />
                    <LineItem label="Staff Welfare" current={pl.staff_welfare} previous={prevPl.staff_welfare} indent={2} />
                    <LineItem label="Finance Costs" current={pl.finance_costs} previous={prevPl.finance_costs} indent={1} />
                    <LineItem label="Interest on Loan" current={pl.interest_on_loan} previous={prevPl.interest_on_loan} indent={2} />
                    <LineItem label="Bank Charges" current={pl.bank_charges} previous={prevPl.bank_charges} indent={2} />
                    <LineItem label="Depreciation & Amortisation" current={pl.depreciation} previous={prevPl.depreciation} indent={1} />
                    <LineItem label="Other Expenses" current={pl.other_expenses} previous={prevPl.other_expenses} indent={1} />
                    <LineItem label="Rent" current={pl.rent} previous={prevPl.rent} indent={2} />
                    <LineItem label="Travelling & Conveyance" current={pl.travelling_conveyance} previous={prevPl.travelling_conveyance} indent={2} />
                    <LineItem label="Selling & Business Promotion" current={pl.selling_business_promotion} previous={prevPl.selling_business_promotion} indent={2} />
                    <LineItem label="Food Expenses" current={pl.food_expenses} previous={prevPl.food_expenses} indent={2} />
                    <LineItem label="Total Expenses (IV)" current={pl.total_expenses} previous={prevPl.total_expenses} bold />
                    <LineItem label="V. Profit/(Loss) Before Tax (III-IV)" current={pl.profit_before_tax} previous={prevPl.profit_before_tax} bold negative />
                    <LineItem label="Deferred Tax" current={pl.deferred_tax} previous={prevPl.deferred_tax} indent={1} negative />
                    <LineItem label="VI. Net Profit/(Loss)" current={pl.net_profit} previous={prevPl.net_profit} bold negative />
                    <LineItem label="EPS - Basic (₹)" current={pl.earnings_per_share_basic} previous={prevPl.earnings_per_share_basic} negative />
                    <LineItem label="EPS - Diluted (₹)" current={pl.earnings_per_share_diluted} previous={prevPl.earnings_per_share_diluted} negative />
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="balance-sheet">
              <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="balance-sheet">
                <div className="bg-[#0F4C81] text-white px-4 py-3">
                  <h3 className="font-semibold text-sm">Balance Sheet</h3>
                  <p className="text-xs opacity-70">All figures in ₹ Thousands</p>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#E2E8F0]">
                      <th className="text-left px-4 py-2 text-xs font-medium text-[#64748B]">Particulars</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-[#64748B]">FY {selectedYear}</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-[#64748B]">FY 2023-24</th>
                    </tr>
                  </thead>
                  <tbody>
                    <LineItem label="EQUITY & LIABILITIES" current={null} previous={null} bold />
                    <LineItem label="Shareholders' Funds" current={null} previous={null} bold />
                    <LineItem label="Share Capital" current={bs.share_capital} previous={prevBs.share_capital} indent={1} />
                    <LineItem label="Reserves & Surplus" current={bs.reserves_surplus} previous={prevBs.reserves_surplus} indent={1} negative />
                    <LineItem label="Non-Current Liabilities" current={null} previous={null} bold />
                    <LineItem label="Long Term Borrowings" current={bs.long_term_borrowings} previous={prevBs.long_term_borrowings} indent={1} />
                    <LineItem label="Deferred Tax Liability" current={bs.deferred_tax_liability} previous={prevBs.deferred_tax_liability} indent={1} />
                    <LineItem label="Other Long Term Liabilities" current={bs.other_long_term_liabilities} previous={prevBs.other_long_term_liabilities} indent={1} />
                    <LineItem label="Current Liabilities" current={null} previous={null} bold />
                    <LineItem label="Trade Payables" current={bs.trade_payables_msme} previous={prevBs.trade_payables_msme} indent={1} />
                    <LineItem label="Other Current Liabilities" current={bs.other_current_liabilities} previous={prevBs.other_current_liabilities} indent={1} />
                    <LineItem label="Short Term Provisions" current={bs.short_term_provisions} previous={prevBs.short_term_provisions} indent={1} />
                    <LineItem label="TOTAL EQUITY & LIABILITIES" current={bs.total_equity_liabilities} previous={prevBs.total_equity_liabilities} bold />
                    <tr><td colSpan={3} className="h-4 bg-[#F8FAFC]"></td></tr>
                    <LineItem label="ASSETS" current={null} previous={null} bold />
                    <LineItem label="Non-Current Assets" current={null} previous={null} bold />
                    <LineItem label="Property, Plant & Equipment" current={bs.property_plant_equipment} previous={prevBs.property_plant_equipment} indent={1} />
                    <LineItem label="Intangible Assets" current={bs.intangible_assets} previous={prevBs.intangible_assets} indent={1} />
                    <LineItem label="Non-Current Investments" current={bs.non_current_investments} previous={prevBs.non_current_investments} indent={1} />
                    <LineItem label="Deferred Tax Asset" current={bs.deferred_tax_asset} previous={prevBs.deferred_tax_asset} indent={1} />
                    <LineItem label="Long Term Loans & Advances" current={bs.long_term_loans_advances} previous={prevBs.long_term_loans_advances} indent={1} />
                    <LineItem label="Current Assets" current={null} previous={null} bold />
                    <LineItem label="Inventories" current={bs.inventories} previous={prevBs.inventories} indent={1} />
                    <LineItem label="Trade Receivables" current={bs.trade_receivables} previous={prevBs.trade_receivables} indent={1} />
                    <LineItem label="Cash & Cash Equivalents" current={bs.cash_equivalents} previous={prevBs.cash_equivalents} indent={1} />
                    <LineItem label="Short Term Loans & Advances" current={bs.short_term_loans_advances} previous={prevBs.short_term_loans_advances} indent={1} />
                    <LineItem label="Other Current Assets" current={bs.other_current_assets} previous={prevBs.other_current_assets} indent={1} />
                    <LineItem label="TOTAL ASSETS" current={bs.total_assets} previous={prevBs.total_assets} bold />
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="ratios">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="key-ratios">
                {[
                  { label: 'Current Ratio', key: 'current_ratio', fmt: (v) => v?.toFixed(2) },
                  { label: 'Debt-Equity Ratio', key: 'debt_equity_ratio', fmt: (v) => v?.toFixed(2) },
                  { label: 'Return on Equity (%)', key: 'return_on_equity', fmt: (v) => `${v?.toFixed(1)}%` },
                  { label: 'Inventory Turnover (days)', key: 'inventory_turnover_days', fmt: (v) => v?.toFixed(1) },
                  { label: 'Receivables Turnover (days)', key: 'trade_receivables_turnover_days', fmt: (v) => v?.toFixed(1) },
                  { label: 'Payables Turnover (days)', key: 'trade_payables_turnover_days', fmt: (v) => v?.toFixed(1) },
                  { label: 'Net Profit Ratio', key: 'net_profit_ratio', fmt: (v) => `${(v * 100)?.toFixed(1)}%` },
                  { label: 'Return on Capital Employed', key: 'return_on_capital_employed', fmt: (v) => `${(v * 100)?.toFixed(1)}%` },
                ].map((r, i) => (
                  <div key={i} className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                    <p className="text-xs text-[#64748B] mb-2">{r.label}</p>
                    <p className="text-xl font-bold font-['Plus_Jakarta_Sans'] text-[#0F172A]">{r.fmt(ratios[r.key])}</p>
                    {prevRatios[r.key] !== undefined && (
                      <p className="text-xs text-[#64748B] mt-1">Prev: {r.fmt(prevRatios[r.key])}</p>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
          </>
        )}
      </div>
    </div>
  );
}
